import { useCallback, useEffect, useMemo, useState } from "react";

// Web-rnw experience model — the console mirror of mobile's useExperience
// (apps/mobile/src/experience). A role-gated "experience" is a distinct
// surface set: athlete (everyone's default), coach (the team console, CO-8),
// and admin (the feedback inbox). The role switch renders only for multi-role
// users; single-role users never see it. Coach appears once the caller has ≥1
// active coach edge (they coach someone). Presentation lives in the account
// menu (AccountMenu) now — this module is pure state.
export type Experience = "athlete" | "admin" | "coach";

const STORAGE_KEY = "jl.experience.v1";
const isExperience = (v: unknown): v is Experience =>
  v === "athlete" || v === "admin" || v === "coach";

export function useExperience(isAdmin: boolean, isCoach: boolean, ready = true) {
  const available = useMemo<Experience[]>(() => {
    const xs: Experience[] = ["athlete"];
    if (isCoach) xs.push("coach");
    if (isAdmin) xs.push("admin");
    return xs;
  }, [isAdmin, isCoach]);

  const [active, setActiveState] = useState<Experience>("athlete");

  // Restore the persisted choice once on mount.
  useEffect(() => {
    const raw =
      typeof window !== "undefined"
        ? window.localStorage.getItem(STORAGE_KEY)
        : null;
    if (isExperience(raw)) setActiveState(raw);
  }, []);

  // Never strand the user on an experience they can't see (e.g. revoked
  // admin, or a persisted 'admin' on a non-admin device).
  //
  // Gated on `ready` (CO-15): `isCoach` is resolved by an async edge read, so
  // until it lands `available` is just ['athlete'] and this guard would wipe a
  // persisted 'coach' choice on every refresh. Admin never showed the bug —
  // user.admin comes off the token synchronously. Mobile's useExperience gates
  // the same guard on ProfileContext's `loaded` for the same reason.
  useEffect(() => {
    if (ready && !available.includes(active)) setActiveState("athlete");
  }, [ready, available, active]);

  const setActive = useCallback((e: Experience) => {
    setActiveState(e);
    if (typeof window !== "undefined")
      window.localStorage.setItem(STORAGE_KEY, e);
  }, []);

  return { available, active, setActive };
}
